import { inlineComputedStyles } from '@terminal/views/alfred/research/utils/copyChartDom';

import { createSizedImage } from './rasterizeClipboardImage';
import { rasterizeHtmlElementToPng } from './rasterizeHtmlElement';

const KEY_STATS_RASTER_HEIGHT_BUFFER_PX = 64;
const KEY_STATS_RASTER_TRIM_PADDING_PX = 4;
const MIN_ROW_FLEX_WIDTH_PX = 120;

const isRowFlex = (el: Element): el is HTMLElement => {
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  const { display, flexDirection } = window.getComputedStyle(el);
  if (display !== 'flex' && display !== 'inline-flex') {
    return false;
  }
  return flexDirection === 'row' || flexDirection === 'row-reverse';
};

const getOutermost = (elements: HTMLElement[]) =>
  elements.filter(
    el => !elements.some(other => other !== el && other.contains(el))
  );

const getOutermostRowFlexContainers = (root: HTMLElement): HTMLElement[] =>
  getOutermost(Array.from(root.querySelectorAll('*')).filter(isRowFlex)).filter(
    el =>
      el.offsetWidth >= MIN_ROW_FLEX_WIDTH_PX &&
      !el.hasAttribute('data-clipboard-preserve') &&
      !el.closest('table')
  );

const getOutermostTables = (root: HTMLElement): HTMLElement[] =>
  getOutermost(Array.from(root.querySelectorAll<HTMLElement>('table'))).filter(
    table => !table.closest('[data-clipboard-preserve]')
  );

// SVG foreignObject flex layout treats overflow:hidden labels as min-width:0, so
// CatalystBadge names collapse to an ellipsis even when the live pill fits.
export const expandCatalystBadgesForClipboard = (root: HTMLElement) => {
  root
    .querySelectorAll<HTMLElement>('[data-testid="catalyst-badge"]')
    .forEach(badge => {
      badge.style.flexShrink = '0';
      badge.style.overflow = 'visible';
      badge.style.width = 'max-content';
      badge.style.minWidth = 'max-content';

      badge.querySelectorAll<HTMLElement>('*').forEach(child => {
        if (window.getComputedStyle(child).textOverflow !== 'ellipsis') {
          return;
        }
        child.style.overflow = 'visible';
        child.style.textOverflow = 'clip';
        child.style.maxWidth = 'none';
        child.style.flexShrink = '0';
        child.style.minWidth = 'max-content';
      });
    });
};

const unlockKeyStatsSnapshotHeights = (element: HTMLElement) => {
  element.style.height = 'auto';
  element.style.gridTemplateRows = 'none';
  element.querySelectorAll<HTMLElement>(':scope > *').forEach(tile => {
    tile.style.height = 'auto';
    tile.style.maxHeight = 'none';
    tile.style.overflow = 'visible';
    tile.style.boxSizing = 'border-box';
  });
};

interface RasterizeElementToImageArgs {
  element: HTMLElement;
  fontCss?: string;
}

const rasterizeElementToImage = async ({
  element,
  fontCss = '',
}: RasterizeElementToImageArgs) => {
  const computed = window.getComputedStyle(element);
  const margin = {
    top: computed.marginTop,
    right: computed.marginRight,
    bottom: computed.marginBottom,
    left: computed.marginLeft,
  };

  const isKeyStats = element.hasAttribute('data-key-stats');
  const isTable = element.tagName === 'TABLE';
  const tableWidth = isTable
    ? Math.ceil(Math.max(element.offsetWidth, element.scrollWidth))
    : 0;
  const tableHeight = isTable
    ? Math.ceil(Math.max(element.offsetHeight, element.scrollHeight))
    : 0;

  inlineComputedStyles(element);
  // offsetHeight excludes margin; if it stays inlined, foreignObject shifts
  // content down and clips the bottom of the snapshot.
  element.style.margin = '0';

  if (isKeyStats) {
    // Inlined used heights / grid rows clip wrapped values; SVG foreignObject
    // font metrics can wrap one more line than the live layout.
    unlockKeyStatsSnapshotHeights(element);
  }
  if (isTable) {
    // width:100% inlines to the scrollport; unlock so wide cohort tables
    // rasterize at content width instead of clipping in overflow-x:auto.
    element.style.overflow = 'visible';
    element.style.maxWidth = 'none';
    element.style.width = `${tableWidth}px`;
  }

  const width = isTable ? tableWidth : Math.ceil(element.offsetWidth);
  const height = Math.ceil(
    isKeyStats
      ? element.scrollHeight + KEY_STATS_RASTER_HEIGHT_BUFFER_PX
      : isTable
        ? tableHeight
        : element.offsetHeight
  );

  const png = await rasterizeHtmlElementToPng({
    element,
    width,
    height,
    fontCss,
    trimPadding: isKeyStats ? KEY_STATS_RASTER_TRIM_PADDING_PX : undefined,
  });
  if (!png) {
    return;
  }

  const image = createSizedImage({ src: png, width, height });
  image.style.display = 'block';
  image.style.maxWidth = '100%';
  image.style.height = 'auto';
  if (isKeyStats || isTable) {
    image.removeAttribute('height');
  }
  image.style.marginTop = margin.top;
  image.style.marginRight = margin.right;
  image.style.marginBottom = margin.bottom;
  image.style.marginLeft = margin.left;
  element.replaceWith(image);
};

const rasterizeElements = async (elements: HTMLElement[], fontCss: string) => {
  for (const element of elements) {
    await rasterizeElementToImage({ element, fontCss });
  }
};

export const rasterizeClipboardSnapshots = async (
  root: HTMLElement,
  fontCss: string
) => {
  await rasterizeElements(
    Array.from(root.querySelectorAll<HTMLElement>('[data-clipboard-snapshot]')),
    fontCss
  );
  // Docs/Outlook/Slack rewrite HTML tables; paste the table as a PNG instead.
  await rasterizeElements(getOutermostTables(root), fontCss);
  await rasterizeElements(getOutermostRowFlexContainers(root), fontCss);
};
